import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { getInquiry, updateInquiry } from '../../api/inquiries';

const ProposalDetail = () => {
    const { id } = useParams();
    // eslint-disable-next-line no-unused-vars
    const { user } = useAuth();
    const navigate = useNavigate();
    const [proposal, setProposal] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProposal = async () => {
            try {
                const data = await getInquiry(id);
                setProposal(data);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchProposal();
    }, [id]);

    const updateStatus = async (newStatus) => {
        if (!window.confirm(`상태를 "${newStatus}"로 변경하시겠습니까?`)) return;

        try {
            const updated = await updateInquiry(id, { status: newStatus });
            if (updated) {
                setProposal({ ...proposal, status: newStatus });
                alert('상태가 변경되었습니다.');
            }
        } catch (error) {
            console.error(error);
            alert('변경 실패');
        }
    };

    if (loading) return <AdminLayout><div className="p-10 text-center text-gray-500 font-bold animate-pulse">데이터 로딩 중...</div></AdminLayout>;
    if (!proposal) return <AdminLayout><div className="p-10 text-center text-gray-500 font-bold">오류: 제안서를 찾을 수 없습니다.</div></AdminLayout>;

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-6">
                <button onClick={() => navigate('/admin/dashboard')} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors shadow-sm outline-none shrink-0">&larr; 목록으로 돌아가기</button>
                <h2 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">{proposal.customerName} 고객님의 문의 상세 정보</h2>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
                {/* Status and Action Buttons */}
                <div className="mb-8 flex flex-col md:flex-row gap-4 md:items-center border-b border-gray-100 pb-6">
                    <div className="shrink-0">
                        <span className={`px-4 py-1.5 rounded-full text-sm font-bold shadow-sm ${
                            proposal.status === 'UNREAD' ? 'bg-red-100 text-red-700' :
                            proposal.status === 'RESPONDED' ? 'bg-blue-100 text-blue-700' :
                            proposal.status === 'DONE' ? 'bg-gray-200 text-gray-700' :
                            'bg-gray-100 text-gray-600'
                        }`}>현재 상태: {proposal.status}</span>
                    </div>
                    
                    <div className="md:ml-auto flex flex-wrap gap-2">
                        <span className="text-xs text-gray-400 font-bold self-center mr-2 hidden xl:inline-block">상태 변경 액션 &rarr;</span>
                        <button className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-bold rounded-lg border border-red-200 transition-colors shadow-sm" onClick={() => updateStatus('UNREAD')}>안읽음</button>
                        <button className="px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-700 text-sm font-bold rounded-lg border border-gray-300 transition-colors shadow-sm" onClick={() => updateStatus('READ')}>읽음 처리</button>
                        <button className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm font-bold rounded-lg border border-blue-200 transition-colors shadow-sm" onClick={() => updateStatus('RESPONDED')}>답변 완료</button>
                        <button className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-lg border border-gray-300 transition-colors shadow-sm" onClick={() => updateStatus('DONE')}>계약/종료</button>
                        <button className="px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-500 text-sm font-bold rounded-lg border border-dashed border-gray-300 transition-colors shadow-sm" onClick={() => updateStatus('REJECTED')}>거절</button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Basic Info Column */}
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">기본 정보</h3>
                        <div className="space-y-3 text-sm text-gray-600 bg-gray-50 p-5 rounded-xl border border-gray-100">
                            <div className="flex"><span className="w-24 font-bold text-gray-500 shrink-0">문의 유형</span> <span className="font-semibold text-gray-900 bg-blue-100 text-blue-700 px-2 rounded">{proposal.type}</span></div>
                            <div className="flex"><span className="w-24 font-bold text-gray-500 shrink-0">회사명</span> <span className="font-semibold text-gray-900">{proposal.companyName || '-'}</span></div>
                            <div className="flex"><span className="w-24 font-bold text-gray-500 shrink-0">고객명</span> <span className="font-semibold text-gray-900">{proposal.customerName}</span></div>
                            <div className="flex"><span className="w-24 font-bold text-gray-500 shrink-0">연락처</span> <span className="font-semibold text-gray-900">{proposal.contactInfo}</span></div>
                            <div className="flex"><span className="w-24 font-bold text-gray-500 shrink-0">이메일</span> <span className="font-semibold text-gray-900">{proposal.email || '-'}</span></div>
                            <div className="flex"><span className="w-24 font-bold text-gray-500 shrink-0">관심 솔루션</span> <span className="font-semibold text-gray-900 break-words">{proposal.targetServices?.join(', ') || '-'}</span></div>
                            <div className="flex"><span className="w-24 font-bold text-gray-500 shrink-0">등록 일시</span> <span className="font-medium text-gray-700">{new Date(proposal.createdAt).toLocaleString('ko-KR')}</span></div>
                        </div>
                    </div>
                    
                    {/* Message Column */}
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">상세 고객 요청 내용</h3>
                        <div className="bg-gray-50 p-5 rounded-xl border border-gray-100 whitespace-pre-wrap text-sm text-gray-700 leading-relaxed shadow-inner min-h-[220px]">
                            {proposal.message}
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default ProposalDetail;
